import type {
  AccessRequestDetail,
  AccessRequestFiltros,
  AccessRequestSummary,
  AccessRequestValidacaoStatus,
  AmbiguousCompanyChallenge,
  Company,
  CreateManagedUserInput,
  ChecklistFormatacaoDetail,
  ChecklistFormatacaoFiltros,
  ChecklistFormatacaoInput,
  ChecklistFormatacaoStatus,
  ChecklistFormatacaoSummary,
  CreateTicketInput,
  FirstAccessValidation,
  LoginInput,
  LoginResult,
  ManagedUser,
  OtpChallenge,
  Pagination,
  PasswordResetValidation,
  RatDetail,
  RatFiltros,
  RatInput,
  RatStatus,
  RatSummary,
  Session,
  SupportUser,
  TicketCategory,
  TicketDetail,
  TicketFilters,
  TicketStatus,
  TicketSummary,
  UpdateManagedUserInput,
} from "../types/api";
import { decodeLegacyJson, normalizeKeys } from "./utils";

const API_URL = (import.meta.env.VITE_API_URL || "/api").replace(/\/$/, "");
const SESSION_KEY = "mira.helpdesk.session";
const DEVICE_KEY = "mira.helpdesk.device";

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export function getStoredSession(): Session | null {
  try {
    const value = sessionStorage.getItem(SESSION_KEY);
    if (!value) return null;
    const session = JSON.parse(value) as Session;
    if (!session.token) return null;
    return session;
  } catch {
    return null;
  }
}

export function storeSession(session: Session | null) {
  if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else sessionStorage.removeItem(SESSION_KEY);
}

export function isOtpChallenge(result: LoginResult): result is OtpChallenge {
  return "otpRequerido" in result && result.otpRequerido === true;
}

export function isAmbiguousCompany(
  result: LoginResult,
): result is AmbiguousCompanyChallenge {
  return "empresaAmbigua" in result && result.empresaAmbigua === true;
}

/**
 * O token do dispositivo confiável vive no localStorage porque precisa
 * sobreviver ao fechamento do navegador — é ele que dispensa o código por
 * 30 dias. Sozinho não dá acesso: a senha continua obrigatória.
 */
function getDeviceToken() {
  try {
    return localStorage.getItem(DEVICE_KEY) ?? "";
  } catch {
    return "";
  }
}

function captureDeviceToken<T>(payload: T): T {
  if (!payload || typeof payload !== "object") return payload;

  const { dispositivo, ...rest } = payload as T & { dispositivo?: string | null };
  if (dispositivo === undefined) return payload;

  try {
    if (dispositivo) localStorage.setItem(DEVICE_KEY, dispositivo);
  } catch {
    // Sem armazenamento o usuário apenas verá o código no próximo login.
  }

  return rest as T;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  authenticated = true,
): Promise<T> {
  const session = getStoredSession();
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json; charset=utf-8");
  }
  headers.set("Accept", "application/json");
  if (authenticated && session?.token) {
    headers.set("Authorization", `Bearer ${session.token}`);
  }

  const response = await fetch(`${API_URL}${path}`, { ...options, headers });
  const contentType = response.headers.get("content-type") ?? "";
  let payload: unknown = null;
  if (response.status !== 204) {
    payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();
  }

  if (!response.ok) {
    if (response.status === 401) {
      storeSession(null);
      window.dispatchEvent(new Event("mira:unauthorized"));
    }
    const decoded = decodeLegacyJson<Record<string, unknown> | string>(payload);
    const message =
      typeof decoded === "string"
        ? decoded
        : String(decoded?.message ?? decoded?.Message ?? "Não foi possível concluir a operação.");
    throw new ApiError(message, response.status, decoded);
  }

  return normalizeKeys<T>(decodeLegacyJson(payload));
}

async function requestBlob(path: string): Promise<Blob> {
  const session = getStoredSession();
  const headers = new Headers({ Accept: "*/*" });
  if (session?.token) {
    headers.set("Authorization", `Bearer ${session.token}`);
  }

  const response = await fetch(`${API_URL}${path}`, { headers });
  if (!response.ok) {
    if (response.status === 401) {
      storeSession(null);
      window.dispatchEvent(new Event("mira:unauthorized"));
    }

    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();
    const decoded = decodeLegacyJson<Record<string, unknown> | string>(payload);
    const message =
      typeof decoded === "string"
        ? decoded
        : String(decoded?.message ?? decoded?.Message ?? "Não foi possível baixar o anexo.");
    throw new ApiError(message, response.status);
  }

  return response.blob();
}

function normalizeAttachmentPath(value: string) {
  try {
    const pathname = new URL(value, window.location.origin).pathname;
    const marker = "/attach";
    const markerIndex = pathname.toLowerCase().indexOf(`${marker}/`);
    const storedPath = markerIndex >= 0 ? pathname.slice(markerIndex + marker.length) : pathname;
    return decodeURIComponent(storedPath);
  } catch {
    return value;
  }
}

function queryString(values: Record<string, string | number | boolean | undefined>) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  return params.toString();
}

export const api = {
  async login(input: LoginInput) {
    const result = await request<LoginResult>(
      "/Home/login",
      {
        method: "POST",
        body: JSON.stringify({
          username: input.username.trim(),
          password: input.password,
          dispositivo: getDeviceToken(),
        }),
      },
      false,
    );
    return captureDeviceToken(result);
  },

  /**
   * Segunda etapa quando o login deu ambíguo entre empresas: reenvia
   * usuário/senha (a pessoa não digita de novo) já com a empresa escolhida.
   * O servidor reconfere a senha antes de seguir para o mesmo desafio de OTP.
   */
  async loginEmpresa(input: LoginInput, codigoEmpresa: number) {
    const result = await request<LoginResult>(
      "/Home/login/empresa",
      {
        method: "POST",
        body: JSON.stringify({
          username: input.username.trim(),
          password: input.password,
          codigoEmpresa,
          dispositivo: getDeviceToken(),
        }),
      },
      false,
    );
    return captureDeviceToken(result);
  },

  async validateLoginOtp(
    desafio: string,
    codigo: string,
    lembrarDispositivo: boolean,
  ) {
    const session = await request<Session>(
      "/Home/login/otp",
      {
        method: "POST",
        body: JSON.stringify({ desafio, codigo, lembrarDispositivo }),
      },
      false,
    );
    return captureDeviceToken(session);
  },

  resendLoginOtp(desafio: string) {
    return request<OtpChallenge>(
      "/Home/login/otp/reenviar",
      {
        method: "POST",
        body: JSON.stringify({ desafio }),
      },
      false,
    );
  },

  requestFirstAccess(email: string, phone: string) {
    return request<{ message: string }>(
      "/Usuarios/primeiro-acesso",
      {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), phone }),
      },
      false,
    );
  },

  validateFirstAccessToken(token: string) {
    return request<FirstAccessValidation>(
      "/Usuarios/primeiro-acesso/validar",
      {
        method: "POST",
        body: JSON.stringify({ token }),
      },
      false,
    );
  },

  activateFirstAccess(token: string, password: string) {
    return request<{ message: string }>(
      "/Usuarios/primeiro-acesso/ativar",
      {
        method: "POST",
        body: JSON.stringify({ token, password }),
      },
      false,
    );
  },

  requestPasswordReset(email: string) {
    return request<{ message: string }>(
      "/Usuarios/recuperar-senha",
      {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
      },
      false,
    );
  },

  validatePasswordResetToken(token: string) {
    return request<PasswordResetValidation>(
      "/Usuarios/recuperar-senha/validar",
      {
        method: "POST",
        body: JSON.stringify({ token }),
      },
      false,
    );
  },

  confirmPasswordReset(token: string, password: string) {
    return request<{ message: string }>(
      "/Usuarios/recuperar-senha/confirmar",
      {
        method: "POST",
        body: JSON.stringify({ token, password }),
      },
      false,
    );
  },

  listTickets(companyId: number, filters: TicketFilters = {}) {
    const query = queryString({
      codigoEmpresa: companyId,
      dtInicial: filters.startDate ?? "",
      dtFinal: filters.endDate ?? "",
      naoFinalizados: filters.onlyOpen ? true : undefined,
      prioridade: filters.priority ?? "",
      pagina: filters.page ?? 1,
      tamPagina: filters.pageSize ?? 10,
      query: filters.query ?? "",
      status: filters.status ?? "TODOS",
      tipo: filters.type ?? "Todos",
      codigoUsuarioMira: filters.assigneeId,
      subCategoriaIds: filters.subCategoriaIds?.length ? filters.subCategoriaIds.join(",") : undefined,
    });
    return request<Pagination<TicketSummary>>(`/Chamados/listar?${query}`);
  },

  getCompanies() {
    return request<Company[]>("/Empresas");
  },

  getSupportUsers() {
    return request<SupportUser[]>("/Usuarios/empresa/52");
  },

  getManagedUsers(
    companyId: number,
    active: boolean | undefined,
    search: string,
  ) {
    const query = queryString({
      empresaId: companyId,
      ativos: active,
      busca: search,
    });
    return request<ManagedUser[]>(`/Usuarios/gestao?${query}`);
  },

  createManagedUser(input: CreateManagedUserInput) {
    return request<{
      codigo: number;
      emailEnviado: boolean;
      message: string;
    }>("/Usuarios/gestao", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  updateManagedUser(userId: number, input: UpdateManagedUserInput) {
    return request<{ message: string }>(`/Usuarios/gestao/${userId}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },

  resendManagedUserAccess(userId: number) {
    return request<{ message: string }>(
      `/Usuarios/gestao/${userId}/reenviar-acesso`,
      { method: "POST" },
    );
  },

  getCategories() {
    return request<TicketCategory[]>("/Chamados/categorias");
  },

  getStatuses() {
    return request<TicketStatus[]>("/Chamados/status");
  },

  getTicket(companyId: number, ticketId: number) {
    return request<TicketDetail>(`/Chamados/${companyId}/${ticketId}`);
  },

  createTicket(input: CreateTicketInput) {
    return request<number>("/Chamados/insere-chamado-web", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  updateTicketType(ticketId: number, tipo: number) {
    return request<void>(`/Chamados/${ticketId}/tipo`, {
      method: "PUT",
      body: JSON.stringify({ tipo }),
    });
  },

  addTicketStep(ticketId: number, statusId: number, description: string) {
    return request<number>(`/Chamados/${ticketId}/tramite-web`, {
      method: "POST",
      body: JSON.stringify({
        codigoStatus: statusId,
        complemento: description,
      }),
    });
  },

  uploadTicketImage(ticketId: number, file: File) {
    const body = new FormData();
    body.append("file", file);
    return request<boolean>(`/Chamados/${ticketId}/uploadFoto`, {
      method: "POST",
      body,
    });
  },

  uploadOpeningAttachments(ticketId: number, files: File[]) {
    const body = new FormData();
    files.forEach((file) => body.append("files", file));
    return request<{ arquivos: string[] }>(
      `/Chamados/${ticketId}/anexos-abertura`,
      {
        method: "POST",
        body,
      },
    );
  },

  uploadTicketAttachments(ticketId: number, stepId: number, files: File[]) {
    const body = new FormData();
    files.forEach((file) => body.append("files", file));
    return request<{ arquivos: string[] }>(
      `/Chamados/${ticketId}/upload-new/${stepId}`,
      {
        method: "POST",
        body,
      },
    );
  },

  downloadTicketAttachment(ticketId: number, attachment: string) {
    const query = new URLSearchParams({
      arquivo: normalizeAttachmentPath(attachment),
    });
    return requestBlob(`/Chamados/${ticketId}/anexo?${query.toString()}`);
  },

  submitAccessRequest(payload: object) {
    return request<{ id: number; chamadoId: number }>(
      "/AccessRequest",
      { method: "POST", body: JSON.stringify(payload) },
    );
  },

  // Empresa do login + empresas vinculadas a ela (ver EmpresaVinculada no
  // banco) — usado só pelo seletor de empresa da Solicitação de Acesso.
  getAccessRequestEmpresasDisponiveis() {
    return request<Company[]>("/AccessRequest/empresas-disponiveis");
  },

  listAccessRequests(filtros: AccessRequestFiltros = {}) {
    const query = new URLSearchParams();
    if (filtros.solicitante) query.set("solicitante", filtros.solicitante);
    if (filtros.empresaSolicitante) query.set("empresaSolicitante", filtros.empresaSolicitante);
    if (filtros.tipo) query.set("tipo", filtros.tipo);
    if (filtros.dataDe) query.set("dataDe", filtros.dataDe);
    if (filtros.dataAte) query.set("dataAte", filtros.dataAte);
    if (filtros.busca) query.set("busca", filtros.busca);
    const suffix = query.toString();
    return request<AccessRequestSummary[]>(`/AccessRequest${suffix ? `?${suffix}` : ""}`);
  },

  getAccessRequest(id: number) {
    return request<AccessRequestDetail[]>(`/AccessRequest/${id}`);
  },

  validateAccessRequest(
    id: number,
    payload: {
      status: AccessRequestValidacaoStatus;
      tecnico: string;
      observacoes?: string | null;
      itensConcluidos?: string[];
    },
  ) {
    return request<{ id: number; status: AccessRequestValidacaoStatus }>(`/AccessRequest/${id}/validar`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  submitChecklistFormatacao(payload: ChecklistFormatacaoInput, status: ChecklistFormatacaoStatus = "concluido") {
    return request<{ id: number; status: ChecklistFormatacaoStatus }>(
      `/ChecklistFormatacao?status=${status}`,
      { method: "POST", body: JSON.stringify(payload) },
    );
  },

  updateChecklistFormatacao(id: number, payload: ChecklistFormatacaoInput, status: ChecklistFormatacaoStatus = "concluido") {
    return request<{ id: number; status: ChecklistFormatacaoStatus }>(
      `/ChecklistFormatacao/${id}?status=${status}`,
      { method: "PUT", body: JSON.stringify(payload) },
    );
  },

  listChecklistsFormatacao(filtros: ChecklistFormatacaoFiltros = {}) {
    const query = new URLSearchParams();
    if (filtros.tecnico) query.set("tecnico", filtros.tecnico);
    if (filtros.empresaAtendida) query.set("empresaAtendida", filtros.empresaAtendida);
    if (filtros.dataDe) query.set("dataDe", filtros.dataDe);
    if (filtros.dataAte) query.set("dataAte", filtros.dataAte);
    if (filtros.busca) query.set("busca", filtros.busca);
    if (filtros.status) query.set("status", filtros.status);
    const suffix = query.toString();
    return request<ChecklistFormatacaoSummary[]>(
      `/ChecklistFormatacao${suffix ? `?${suffix}` : ""}`,
    );
  },

  getChecklistFormatacao(id: number) {
    return request<ChecklistFormatacaoDetail[]>(`/ChecklistFormatacao/${id}`);
  },

  async getChecklistByChamado(chamadoId: number) {
    try {
      return await request<ChecklistFormatacaoDetail[]>(`/ChecklistFormatacao/por-chamado/${chamadoId}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  submitRat(payload: RatInput, status: RatStatus = "concluido") {
    return request<{ id: number; chamadoId: number | null; status: RatStatus }>(
      `/Rat?status=${status}`,
      { method: "POST", body: JSON.stringify(payload) },
    );
  },

  updateRat(id: number, payload: RatInput, status: RatStatus = "concluido") {
    return request<{ id: number; chamadoId: number | null; status: RatStatus }>(
      `/Rat/${id}?status=${status}`,
      { method: "PUT", body: JSON.stringify(payload) },
    );
  },

  listRats(filtros: RatFiltros = {}) {
    const query = new URLSearchParams();
    if (filtros.tecnico) query.set("tecnico", filtros.tecnico);
    if (filtros.empresaAtendida) query.set("empresaAtendida", filtros.empresaAtendida);
    if (filtros.dataDe) query.set("dataDe", filtros.dataDe);
    if (filtros.dataAte) query.set("dataAte", filtros.dataAte);
    if (filtros.busca) query.set("busca", filtros.busca);
    if (filtros.status) query.set("status", filtros.status);
    const suffix = query.toString();
    return request<RatSummary[]>(`/Rat${suffix ? `?${suffix}` : ""}`);
  },

  getRat(id: number) {
    return request<RatDetail[]>(`/Rat/${id}`);
  },

  async getRatByChamado(chamadoId: number) {
    try {
      return await request<RatDetail[]>(`/Rat/por-chamado/${chamadoId}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

};
